import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Paper } from '@mui/material';
import { Page } from '@tassomutuo-backoffice/types';
import { toast } from 'react-toastify';

import {
  CheckboxInput,
  ConfirmActionModal,
  CtaButton,
  PageContentLayout,
} from '@tassomutuo-backoffice/ui';

import {
  deletePage,
  getAllPages,
  getAllPagesInProd,
  publishMultiplePagesInProd,
  publishPageInProd,
  syncMultiplePagesWithCms,
  syncPageWithCms,
  togglePageHighlightedInGlossary,
} from '../../../services/api/page/page';
import PageTableRenderCellAction from './components/PageTableRenderCellAction';
import SyncPageInput from './components/SyncPageInput';
import configEnv from '../../../config/config';
import PageTableStatusTassoRenderCell from './components/PageTableStatusTassoRenderCell';
import SelectedItemActionSection from './components/SelectedItemActionSection';

const PagesListPage = () => {
  const queryClient = useQueryClient();

  const {
    isPending: pagesIsPending,
    data: pages,
    error: pagesError,
  } = useQuery({
    queryKey: ['Pages'],
    queryFn: getAllPages,
  });

  const { data: prodPages } = useQuery({
    queryKey: ['ProductionPages'],
    queryFn: getAllPagesInProd,
  });

  const deletePageMutation = useMutation({
    mutationFn: deletePage,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['Pages'],
      });
      toast.success('Pagina eliminata!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const pushPageInProdMutation = useMutation({
    mutationFn: publishPageInProd,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['Pages'],
      });
      queryClient.invalidateQueries({
        queryKey: ['ProductionPages'],
      });
      toast.success('Pagina pubblicata in produzione!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const togglePageHighlightedInGlossaryMutation = useMutation({
    mutationFn: togglePageHighlightedInGlossary,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['Pages'],
      });
      queryClient.invalidateQueries({
        queryKey: ['ProductionPages'],
      });
      toast.success('Pagina aggiornata con successo!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const pushSelectedPagesInProdMutation = useMutation({
    mutationFn: publishMultiplePagesInProd,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['Pages'],
      });
      queryClient.invalidateQueries({
        queryKey: ['ProductionPages'],
      });
      toast.success('Pagine pubblicate in produzione!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const [pageToDelete, setPageToDelete] = useState<null | Page>(null);
  const [pageToPushInProd, setPageToPushInProd] = useState<null | Page>(null);
  const [selectedItemsId, setSelectedItemsId] = useState<string[]>([]);
  const [
    pageToToggleHighlightedInGlossary,
    setPageToToggleHighlightedInGlossary,
  ] = useState<null | Page>(null);

  const onConfirmDeletePage = async () => {
    if (pageToDelete) {
      await deletePageMutation.mutateAsync(pageToDelete?.id);
    }
  };

  const onConfirmPushPageInProd = async () => {
    if (pageToPushInProd) {
      await pushPageInProdMutation.mutateAsync(pageToPushInProd.id);
    }
  };

  const onConfirmToggleHighlightedInGlossary = async () => {
    if (pageToToggleHighlightedInGlossary) {
      await togglePageHighlightedInGlossaryMutation.mutateAsync(
        pageToToggleHighlightedInGlossary.id
      );
    }
  };

  const onSyncPageClick = async (page: Page) => {
    try {
      await syncPageWithCms(page.data.type, page.data.id);
      queryClient.invalidateQueries({
        queryKey: ['Pages'],
      });
      queryClient.invalidateQueries({
        queryKey: ['ProductionPages'],
      });
      toast.success('Pagina sincronizzata!');
    } catch (error) {
      toast.error(
        'Si è verificato un errore durante la sincronizzazione, riprova!'
      );
    }
  };

  const syncCmsContent = async (
    contentType: 'page' | 'post',
    idCms: number
  ) => {
    try {
      await syncPageWithCms(contentType, idCms);
      queryClient.invalidateQueries({
        queryKey: ['Pages'],
      });
      queryClient.invalidateQueries({
        queryKey: ['ProductionPages'],
      });
      toast.success('Contenuto sincronizzato!');
    } catch (error) {
      toast.error(
        'Si è verificato un errore durante la sincronizzazione, riprova!'
      );
    }
  };

  const syncSelectedPages = async () => {
    const pagesData = selectedItemsId
      .map((id) => {
        const page = pages?.find((page) => page.id === id);
        if (page?.data?.type && page?.data?.id) {
          return { contentType: page?.data?.type, idCms: page?.data?.id };
        }
        return null;
      })
      .filter(
        (item): item is { contentType: 'page' | 'post'; idCms: number } =>
          item !== null
      ); // Type guard

    try {
      await syncMultiplePagesWithCms(pagesData);
      queryClient.invalidateQueries({
        queryKey: ['Pages'],
      });
      queryClient.invalidateQueries({
        queryKey: ['ProductionPages'],
      });
      toast.success('Pagine sincronizzate!');
    } catch (error) {
      toast.error(
        'Si è verificato un errore durante la sincronizzazione, riprova!'
      );
    }
  };

  const pushToProdSelectedPages = async () => {
    await pushSelectedPagesInProdMutation.mutateAsync(selectedItemsId);
  };

  const tableColumns: GridColDef<Page>[] = [
    {
      field: 'idWp',
      headerName: 'ID WP',
      minWidth: 100,
      valueGetter: ({ row }) => row.data.id,
    },
    {
      field: 'path',
      headerName: 'Path',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Stato WP',
      flex: 1,
      valueGetter: ({ row }) => row.data.status,
    },
    {
      field: 'isHighlightedInGlossary',
      headerName: 'In Evidenza Glossario',
      flex: 1,
      // maxWidth: 150,
      type: 'boolean',
      valueGetter: ({ row }) => !!row.isHighlightedInGlossary,
      renderCell: ({ row }) => {
        const checked = row.isHighlightedInGlossary;

        return (
          <Box display="flex" justifyContent="center" width="100%">
            <CheckboxInput
              checked={checked}
              onChange={() => setPageToToggleHighlightedInGlossary(row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'statusTasso',
      headerName: 'Stato Tasso',
      flex: 1,
      renderCell: ({ row }) =>
        PageTableStatusTassoRenderCell({
          page: row,
          prodPages,
        }),
    },
    {
      headerName: ' ',
      field: 'push-to-prod',
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => {
        if (!configEnv.isStage) return null;
        return (
          <CtaButton
            variant="contained"
            onClick={() => setPageToPushInProd(row)}
          >
            Pubblica in prod
          </CtaButton>
        );
      },
      minWidth: 130,
    },
    {
      headerName: ' ',
      field: ' ',
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) =>
        PageTableRenderCellAction({
          page: row,
          onDeleteClick: () => setPageToDelete(row),
          onSyncClick: async () => onSyncPageClick(row),
        }),
      minWidth: 130,
    },
  ];

  return (
    <PageContentLayout
      isLoading={pagesIsPending}
      title="Gestione Pagine"
      error={pagesError}
    >
      {configEnv.isStage && (
        <Box>
          <SyncPageInput submit={syncCmsContent} />
          {selectedItemsId?.length > 0 && (
            <SelectedItemActionSection
              selectedItemsId={selectedItemsId}
              onConfirmSyncSelection={syncSelectedPages}
              onConfirmPushSelection={pushToProdSelectedPages}
            />
          )}
        </Box>
      )}
      <Paper>
        {pages && pages?.length > 0 && (
          <DataGrid
            columns={tableColumns}
            rows={pages}
            disableRowSelectionOnClick
            autoHeight
            checkboxSelection
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setSelectedItemsId(newRowSelectionModel as string[]);
            }}
            rowSelectionModel={selectedItemsId}
          />
        )}
      </Paper>
      <ConfirmActionModal
        open={!!pageToDelete}
        handleClose={() => setPageToDelete(null)}
        title="Elimina pagina"
        description={`Sei sicuro di voler eliminare la pagina ${pageToDelete?.path}?`}
        onConfirm={onConfirmDeletePage}
      />
      <ConfirmActionModal
        open={!!pageToPushInProd}
        handleClose={() => setPageToPushInProd(null)}
        title="Pubblica in produzione"
        description={`Sei sicuro di voler pubblicare in produzione la pagina con ID WP ${pageToPushInProd?.data.id}?`}
        onConfirm={onConfirmPushPageInProd}
      />
      <ConfirmActionModal
        open={!!pageToToggleHighlightedInGlossary}
        handleClose={() => setPageToToggleHighlightedInGlossary(null)}
        title="Modifica evidenza in Glossario"
        description={`Sei sicuro di voler ${
          pageToToggleHighlightedInGlossary?.isHighlightedInGlossary
            ? "rimuovere l'evidenza"
            : 'mettere in evidenza'
        }  nel glossario la pagina con ID WP ${
          pageToToggleHighlightedInGlossary?.data.id
        }?`}
        onConfirm={onConfirmToggleHighlightedInGlossary}
      />
    </PageContentLayout>
  );
};

export default PagesListPage;
